import { useEffect, useState } from "react";
import type { Player } from "../../shared/models/Player";
import {
  assignRosterBySlots,
  buildGenericPickName,
  buildLeagueSettingsSummary,
  buildSpecificPickName,
  fetchJsonOrThrow,
  getSleeperAvatarUrl,
  parseSleeperIdentifier,
  slugifyPickId,
  sortPlayersByValue,
  type DynastyRosterPlayer,
  type DynastyRosterTeam,
  type DynastyTeamTradeHistoryAsset,
  type DynastyTeamTradeHistoryEntry,
  type LeagueSettingsSummary,
  type SleeperDraft,
  type SleeperLeague,
  type SleeperRoster,
  type SleeperTransaction,
  type SleeperTradedPick,
  type SleeperUser,
  withCacheBust,
} from "../features/shared/dynastyTradingPageUtils";

const STORAGE_KEY = "fantasy-board:dynasty-trading:sleeper-league";
const SLEEPER_PLAYERS_CACHE_KEY = "fantasy-board:dynasty-trading:sleeper-players:v1";
const SLEEPER_PLAYERS_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const MAX_SLEEPER_TRANSACTION_ROUND = 18;

type UseDynastyLeagueImportArgs = {
  defaultLeagueIdentifier?: string;
  persistLeagueIdentifier?: boolean;
  playersById: Record<string, Player | undefined>;
  resolveCanonicalId: (player: {
    id?: string;
    name?: string;
    team?: string;
    position?: string;
  }) => string | null | undefined;
};

type SleeperPlayerRecord = {
  player_id?: string | number;
  full_name?: string;
  search_full_name?: string;
  first_name?: string;
  last_name?: string;
  team?: string;
  team_abbr?: string;
  position?: string;
  fantasy_positions?: string[];
};

function buildSleeperPlayerName(record: SleeperPlayerRecord | undefined) {
  const fullName = String(record?.full_name ?? record?.search_full_name ?? "").trim();
  if (fullName) return fullName;

  return `${String(record?.first_name ?? "").trim()} ${String(record?.last_name ?? "").trim()}`.trim();
}

function normalizeSleeperPosition(record: SleeperPlayerRecord | undefined) {
  const fantasyPositions = record && Array.isArray(record.fantasy_positions) ? record.fantasy_positions : [];
  const values = [record?.position, ...fantasyPositions];

  for (const value of values) {
    const normalized = String(value ?? "").toUpperCase().trim();
    if (!normalized) continue;
    if (normalized === "D/ST" || normalized === "DEF") return "DST";
    if (normalized === "PK") return "K";
    return normalized;
  }

  return "";
}

function buildSleeperPlayerFallback(playerId: string, record: SleeperPlayerRecord | undefined): Player | undefined {
  const id = String(record?.player_id ?? playerId).trim();
  const name = buildSleeperPlayerName(record);
  if (!id || !name) return undefined;

  return {
    id,
    name,
    position: normalizeSleeperPosition(record) as Player["position"],
    team: String(record?.team ?? record?.team_abbr ?? "").trim(),
  };
}

function readCachedSleeperPlayers() {
  if (typeof window === "undefined") return {};

  try {
    const cached = JSON.parse(window.localStorage.getItem(SLEEPER_PLAYERS_CACHE_KEY) ?? "{}") as {
      savedAt?: number;
      players?: Record<string, SleeperPlayerRecord>;
    };

    if (
      cached.savedAt &&
      Date.now() - cached.savedAt < SLEEPER_PLAYERS_CACHE_MAX_AGE_MS &&
      cached.players &&
      typeof cached.players === "object"
    ) {
      return cached.players;
    }
  } catch {}

  return {};
}

async function getSleeperPlayersIndex() {
  const cached = readCachedSleeperPlayers();
  if (Object.keys(cached).length) return cached;

  const players = await fetchJsonOrThrow<Record<string, SleeperPlayerRecord>>(
    withCacheBust("https://api.sleeper.app/v1/players/nfl")
  );

  try {
    window.localStorage.setItem(
      SLEEPER_PLAYERS_CACHE_KEY,
      JSON.stringify({
        savedAt: Date.now(),
        players,
      })
    );
  } catch {}

  return players && typeof players === "object" ? players : {};
}

function getRosterDisplayName(rosterId: number, owner: SleeperUser | undefined) {
  return (
    owner?.metadata?.team_name?.trim() ||
    owner?.display_name?.trim() ||
    owner?.username?.trim() ||
    `Roster ${rosterId}`
  );
}

function getPlayerDisplayName(playerId: string, playersById: Record<string, Player | undefined>) {
  return playersById[playerId]?.name ?? `Player ${playerId}`;
}

function buildTransactionPickLabel(
  pick: {
    season?: string | number;
    round?: string | number;
    roster_id?: string | number;
  },
  ownerSlotByRosterId: Map<number, number>,
  defaultSeason: number
) {
  const season = Number(pick.season ?? 0) || defaultSeason;
  const round = Number(pick.round ?? 0);
  const originalRosterId = Number(pick.roster_id ?? 0);
  if (!round) return `${season} Pick`;
  const slot = ownerSlotByRosterId.get(originalRosterId);
  return slot ? buildSpecificPickName(season, round, slot) : buildGenericPickName(season, round);
}

function buildTeamTradeHistory(
  rosterId: number,
  transactions: SleeperTransaction[],
  rosterNameById: Map<number, string>,
  ownerSlotByRosterId: Map<number, number>,
  playersById: Record<string, Player | undefined>,
  defaultSeason: number
) {
  return transactions.flatMap((transaction) => {
    const involvedRosterIds = Array.isArray(transaction.roster_ids)
      ? transaction.roster_ids.map((value) => Number(value)).filter((value) => Number.isFinite(value) && value > 0)
      : [];
    if (!involvedRosterIds.includes(rosterId)) return [];

    const sent: DynastyTeamTradeHistoryAsset[] = [];
    const received: DynastyTeamTradeHistoryAsset[] = [];

    Object.entries(transaction.adds ?? {}).forEach(([playerId, teamId]) => {
      const normalizedRosterId = Number(teamId);
      const asset = {
        kind: "player" as const,
        label: getPlayerDisplayName(playerId, playersById),
      };
      if (normalizedRosterId === rosterId) received.push(asset);
      else sent.push(asset);
    });

    (Array.isArray(transaction.draft_picks) ? transaction.draft_picks : []).forEach((pick) => {
      const previousOwnerId = Number(pick.previous_owner_id ?? pick.roster_id ?? 0);
      const ownerId = Number(pick.owner_id ?? 0);
      const asset = {
        kind: "pick" as const,
        label: buildTransactionPickLabel(pick, ownerSlotByRosterId, defaultSeason),
      };
      if (previousOwnerId === rosterId) sent.push(asset);
      if (ownerId === rosterId) received.push(asset);
    });

    (Array.isArray(transaction.waiver_budget) ? transaction.waiver_budget : []).forEach((entry) => {
      const sender = Number(entry.sender ?? 0);
      const receiver = Number(entry.receiver ?? 0);
      const amount = Number(entry.amount ?? 0);
      if (!amount) return;
      const asset = {
        kind: "faab" as const,
        label: `$${amount} FAAB`,
      };
      if (sender === rosterId) sent.push(asset);
      if (receiver === rosterId) received.push(asset);
    });

    const counterpartyRosterIds = involvedRosterIds.filter((value) => value !== rosterId);
    const counterpartyNames = counterpartyRosterIds.map((value) => rosterNameById.get(value) ?? `Roster ${value}`);

    return [{
      transactionId: String(transaction.transaction_id ?? `${transaction.created ?? Date.now()}-${rosterId}`),
      createdAt: Number(transaction.created ?? transaction.status_updated ?? 0) || null,
      leg: Number(transaction.leg ?? 0) || null,
      counterpartyRosterIds,
      counterpartyNames,
      sent,
      received,
    } satisfies DynastyTeamTradeHistoryEntry];
  });
}

export function useDynastyLeagueImport(args: UseDynastyLeagueImportArgs) {
  const {
    defaultLeagueIdentifier = "",
    persistLeagueIdentifier = true,
    playersById,
    resolveCanonicalId,
  } = args;
  const [leagueIdentifier, setLeagueIdentifier] = useState(() => {
    if (defaultLeagueIdentifier) return defaultLeagueIdentifier;
    if (typeof window === "undefined") return "";
    return window.localStorage.getItem(STORAGE_KEY) ?? "";
  });
  const [isImportingLeague, setIsImportingLeague] = useState(false);
  const [leagueError, setLeagueError] = useState("");
  const [leagueName, setLeagueName] = useState("");
  const [leagueSeason, setLeagueSeason] = useState<number | null>(null);
  const [leagueSettings, setLeagueSettings] = useState<LeagueSettingsSummary | null>(null);
  const [importedTeams, setImportedTeams] = useState<DynastyRosterTeam[]>([]);
  const [importProgress, setImportProgress] = useState(0);
  const [lastImportedAt, setLastImportedAt] = useState(0);

  useEffect(() => {
    if (!persistLeagueIdentifier) return;
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, leagueIdentifier);
  }, [leagueIdentifier, persistLeagueIdentifier]);

  async function handleImportLeague() {
    const identifier = parseSleeperIdentifier(leagueIdentifier);
    if (!identifier) {
      setLeagueError("Enter a Sleeper league ID or league URL.");
      return;
    }

    setIsImportingLeague(true);
    setLeagueError("");
    setImportProgress(8);

    try {
      const league = await fetchJsonOrThrow<SleeperLeague>(
        withCacheBust(`https://api.sleeper.app/v1/league/${encodeURIComponent(identifier)}`)
      );
      setImportProgress(20);
      const maxTransactionRound = Math.max(
        MAX_SLEEPER_TRANSACTION_ROUND,
        Number(league.settings?.playoff_week_start ?? 0) || 0
      );
      const transactionRounds = Array.from(
        { length: Math.max(1, maxTransactionRound + 1) },
        (_, index) => index
      );
      const [users, rosters, tradedPicks, draft, transactionRoundsData, sleeperPlayersIndex] = await Promise.all([
        fetchJsonOrThrow<SleeperUser[]>(
          withCacheBust(`https://api.sleeper.app/v1/league/${encodeURIComponent(identifier)}/users`)
        ),
        fetchJsonOrThrow<SleeperRoster[]>(
          withCacheBust(`https://api.sleeper.app/v1/league/${encodeURIComponent(identifier)}/rosters`)
        ),
        fetchJsonOrThrow<SleeperTradedPick[]>(
          withCacheBust(`https://api.sleeper.app/v1/league/${encodeURIComponent(identifier)}/traded_picks`)
        ).catch(() => []),
        league.draft_id
          ? fetchJsonOrThrow<SleeperDraft>(
              withCacheBust(`https://api.sleeper.app/v1/draft/${encodeURIComponent(String(league.draft_id))}`)
            ).catch(() => null)
          : Promise.resolve(null),
        Promise.all(
          transactionRounds.map((round) =>
            fetchJsonOrThrow<SleeperTransaction[]>(
              withCacheBust(`https://api.sleeper.app/v1/league/${encodeURIComponent(identifier)}/transactions/${round}`)
            ).catch(() => [])
          )
        ).catch(() => []),
        getSleeperPlayersIndex().catch((): Record<string, SleeperPlayerRecord> => ({})),
      ]);
      setImportProgress(58);

      const usersById = new Map(
        (Array.isArray(users) ? users : []).map((user) => [String(user.user_id ?? ""), user])
      );
      const settingsSummary = buildLeagueSettingsSummary(league);
      const rosterList = Array.isArray(rosters) ? rosters : [];
      const importedPlayersById: Record<string, Player | undefined> = { ...playersById };
      const pickAssetsByRosterId = new Map<number, DynastyRosterPlayer[]>();
      const defaultSeason = Number(league.season ?? new Date().getFullYear());
      const draftSource = draft ?? league;
      const draftRounds = Math.max(1, Number((draft as SleeperDraft | null)?.settings?.rounds ?? 4) || 4);
      const ownerSlotByRosterId = new Map<number, number>();
      const rosterNameById = new Map<number, string>();

      Object.entries(draftSource.slot_to_roster_id ?? {}).forEach(([slotKey, rosterIdValue]) => {
        const slot = Number(slotKey);
        const rosterId = Number(rosterIdValue);
        if (slot && rosterId) ownerSlotByRosterId.set(rosterId, slot);
      });

      if (!ownerSlotByRosterId.size) {
        const draftOrderByUserId = new Map<string, number>(
          Object.entries(draftSource.draft_order ?? {}).map(([userId, slot]) => [String(userId), Number(slot)])
        );
        rosterList.forEach((roster, index) => {
          const rosterId = Number(roster.roster_id ?? index + 1);
          const slot = draftOrderByUserId.get(String(roster.owner_id ?? ""));
          if (slot) ownerSlotByRosterId.set(rosterId, slot);
        });
      }

      if (!ownerSlotByRosterId.size) {
        rosterList.forEach((roster, index) => {
          ownerSlotByRosterId.set(Number(roster.roster_id ?? index + 1), index + 1);
        });
      }

      rosterList.forEach((roster, index) => {
        const rosterId = Number(roster.roster_id ?? index + 1);
        const owner = usersById.get(String(roster.owner_id ?? ""));
        rosterNameById.set(rosterId, getRosterDisplayName(rosterId, owner));
      });

      rosterList.forEach((roster) => {
        (Array.isArray(roster.players) ? roster.players : []).forEach((playerId) => {
          if (importedPlayersById[playerId]) return;
          importedPlayersById[playerId] = buildSleeperPlayerFallback(playerId, sleeperPlayersIndex[playerId]);
        });
      });

      const completedTradeTransactions = Array.from(
        new Map(
          (Array.isArray(transactionRoundsData) ? transactionRoundsData.flat() : [])
            .filter((transaction) => String(transaction?.status ?? "").toLowerCase() === "complete")
            .filter((transaction) => String(transaction?.type ?? "").toLowerCase() === "trade")
            .map((transaction) => [String(transaction.transaction_id ?? `${transaction.created ?? Math.random()}`), transaction])
        ).values()
      ).sort((left, right) => {
        const leftCreated = Number(left.created ?? left.status_updated ?? 0);
        const rightCreated = Number(right.created ?? right.status_updated ?? 0);
        return rightCreated - leftCreated;
      });
      setImportProgress(74);

      rosterList.forEach((roster, index) => {
        const rosterId = Number(roster.roster_id ?? index + 1);
        const picks: DynastyRosterPlayer[] = [];

        for (let season = defaultSeason; season <= defaultSeason + 2; season += 1) {
          for (let round = 1; round <= draftRounds; round += 1) {
            const originalOwnerSlot = ownerSlotByRosterId.get(rosterId) ?? null;
            picks.push({
              id: `${slugifyPickId(buildGenericPickName(season, round))}-owner-${rosterId}`,
              name:
                season === defaultSeason && originalOwnerSlot
                  ? buildSpecificPickName(season, round, originalOwnerSlot)
                  : buildGenericPickName(season, round),
              position: "PI",
              team: "",
              value: null,
              pickSeason: season,
              pickRound: round,
              pickSlot: season === defaultSeason ? originalOwnerSlot ?? undefined : undefined,
            });
          }
        }

        pickAssetsByRosterId.set(rosterId, picks);
      });

      (Array.isArray(tradedPicks) ? tradedPicks : []).forEach((pick) => {
        const season = Number(pick.season ?? 0);
        const round = Number(pick.round ?? 0);
        const originalOwnerId = Number(pick.roster_id ?? 0);
        const newOwnerId = Number(pick.owner_id ?? originalOwnerId);
        if (!season || !round || !newOwnerId || !originalOwnerId) return;

        const removeFrom = pickAssetsByRosterId.get(originalOwnerId) ?? [];
        const pickIndex = removeFrom.findIndex((asset) => asset.pickSeason === season && asset.pickRound === round);
        if (pickIndex === -1) return;
        const [movedPick] = removeFrom.splice(pickIndex, 1);
        pickAssetsByRosterId.set(originalOwnerId, removeFrom);
        pickAssetsByRosterId.set(newOwnerId, [...(pickAssetsByRosterId.get(newOwnerId) ?? []), movedPick]);
      });

      const teams = rosterList
        .map((roster, index) => {
          const rosterId = Number(roster.roster_id ?? index + 1);
          const owner = usersById.get(String(roster.owner_id ?? ""));
          const rawPlayerIds = Array.isArray(roster.players) ? roster.players : [];

          const players = sortPlayersByValue(
            [
              ...rawPlayerIds.map((playerId) => {
                const liveSleeperPlayer = buildSleeperPlayerFallback(playerId, sleeperPlayersIndex[playerId]);
                const sleeperPlayer = playersById[playerId] ?? importedPlayersById[playerId] ?? liveSleeperPlayer;
                const canonicalId =
                  resolveCanonicalId({
                    id: playerId,
                    name: sleeperPlayer?.name,
                    team: sleeperPlayer?.team,
                    position: sleeperPlayer?.position,
                  }) ?? playerId;
                const player =
                  playersById[canonicalId] ?? importedPlayersById[canonicalId] ?? sleeperPlayer ?? liveSleeperPlayer;
                const value =
                  settingsSummary.valueModeLabel === "2QB"
                    ? player?.sfValue ?? player?.value ?? null
                    : player?.value ?? player?.sfValue ?? null;

                return {
                  id: canonicalId,
                  name: player?.name ?? `Unknown Player (${playerId})`,
                  position: player?.position ?? "",
                  team: player?.team ?? "",
                  value,
                  imageUrl: player?.imageUrl,
                };
              }),
              ...(pickAssetsByRosterId.get(rosterId) ?? []),
            ]
          );

          return {
            rosterId,
            displayName: rosterNameById.get(rosterId) ?? getRosterDisplayName(rosterId, owner),
            avatarUrl: getSleeperAvatarUrl(owner?.avatar),
            playerCount: players.length,
            totalValue: players.reduce((sum, player) => sum + (player.value ?? 0), 0),
            players,
            tradeHistory: buildTeamTradeHistory(
              rosterId,
              completedTradeTransactions,
              rosterNameById,
              ownerSlotByRosterId,
              importedPlayersById,
              defaultSeason
            ),
            ...assignRosterBySlots(players, settingsSummary.starterSlots),
          } satisfies DynastyRosterTeam;
        })
        .sort((left, right) => {
          if (right.totalValue !== left.totalValue) return right.totalValue - left.totalValue;
          return left.displayName.localeCompare(right.displayName);
        });
      setImportProgress(92);

      setLeagueName(league?.name?.trim() || `Sleeper League ${identifier}`);
      setLeagueSeason(Number(league.season ?? new Date().getFullYear()) || null);
      setLeagueSettings(settingsSummary);
      setImportedTeams(teams);
      setImportProgress(100);
      setLastImportedAt(Date.now());
    } catch (error) {
      setLeagueError(error instanceof Error ? error.message : "Failed to import Sleeper league.");
      setImportedTeams([]);
      setLeagueName("");
      setLeagueSeason(null);
      setLeagueSettings(null);
      setLastImportedAt(Date.now());
    } finally {
      setIsImportingLeague(false);
    }
  }

  return {
    handleImportLeague,
    importProgress,
    importedTeams,
    isImportingLeague,
    lastImportedAt,
    leagueError,
    leagueIdentifier,
    leagueName,
    leagueSeason,
    leagueSettings,
    setLeagueIdentifier,
  };
}
